import { _ as _objectWithoutProperties, a as _extends, d as _objectSpread2 } from '../_rollupPluginBabelHelpers-b0164b33.js';
import React from 'react';
import PropTypes from 'prop-types';
import Bem from '../packages/honey-bem.js';
import { Injecss } from '../packages/injecss.js';
import { L as Label } from '../index-c4501d32.js';

var styles = {
raw: ".hcr-textarea-1-6-0{position:relative}.hcr-textarea--error-1-6-0:before,.hcr-textarea--valid-1-6-0:before{display:inline-block;width:24px;height:24px;content:\"\";vertical-align:middle;position:absolute;top:10px;right:12px;z-index:1}@media (min-width:1200px){.hcr-textarea--error-1-6-0:before,.hcr-textarea--valid-1-6-0:before{top:30px}}@media (min-width:1200px){.hcr-textarea--no-label-1-6-0:before{top:6px}}.hcr-textarea--error-1-6-0:before{background:url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 1000'%3E%3Cpath d='M500 90.9A409.1 409.1 0 10909.1 500 409.1 409.1 0 00500 90.9zm-28.2 150a28.2 28.2 0 0156.4 0v373.6a28.2 28.2 0 11-56.4 0zM500 803.6a45.5 45.5 0 1145.5-45.4 45.5 45.5 0 01-45.5 45.4z' fill='rgba(255, 87, 0, 0.99)'/%3E%3C/svg%3E\") no-repeat 50%}.hcr-textarea--valid-1-6-0:before{background:url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 1000'%3E%3Cpath d='M500 90.9A409.1 409.1 0 10909.1 500 409.1 409.1 0 00500 90.9zm257.3 276.4L460 664.5a23.6 23.6 0 01-15.9 6.4 22.7 22.7 0 01-15.9-6.4L242.7 479.1a22.3 22.3 0 010-31.4 21.8 21.8 0 0131.8 0l169.6 169.1 283.2-281.3a22.3 22.3 0 1131.8 31.8z' fill='rgba(115, 215, 0, 0.99)'/%3E%3C/svg%3E\") no-repeat 50%}.hcr-textarea__field-1-6-0{font-family:Roboto Condensed,Arial,sans-serif;font-size:16px;font-weight:300;position:relative;width:100%;min-height:112px;padding:10px 12px;border:1px solid #c8c8c8;border-radius:5px;background-color:#fff;color:#444;line-height:1.375em;overflow:auto;vertical-align:top;-webkit-appearance:none;-moz-appearance:none;appearance:none}@media (min-width:1200px){.hcr-textarea__field-1-6-0{min-height:120px;padding:5px 12px}}.hcr-textarea__field-1-6-0::-webkit-input-placeholder{color:#757575;font-style:italic}.hcr-textarea__field-1-6-0::-moz-placeholder{color:#757575;font-style:italic}.hcr-textarea__field-1-6-0:-ms-input-placeholder{color:#757575;font-style:italic}.hcr-textarea__field-1-6-0::-ms-input-placeholder{color:#757575;font-style:italic}.hcr-textarea__field-1-6-0::placeholder{color:#757575;font-style:italic}.hcr-textarea--valid-1-6-0 .hcr-textarea__field-1-6-0{padding-right:40px}.hcr-textarea--error-1-6-0 .hcr-textarea__field-1-6-0{padding-right:40px;border:1px solid #ff5700}.hcr-textarea--active-1-6-0 .hcr-textarea__field-1-6-0,.hcr-textarea__field-1-6-0:focus{border:1px solid #757575;outline:none;color:#444}.hcr-textarea--disabled-1-6-0 .hcr-textarea__field-1-6-0,.hcr-textarea__field-1-6-0[disabled]{cursor:not-allowed;opacity:.5}",
map:{"hcr-textarea":"hcr-textarea-1-6-0","hcr-textarea--error":"hcr-textarea--error-1-6-0","hcr-textarea--valid":"hcr-textarea--valid-1-6-0","hcr-textarea--no-label":"hcr-textarea--no-label-1-6-0","hcr-textarea__field":"hcr-textarea__field-1-6-0","hcr-textarea--active":"hcr-textarea--active-1-6-0","hcr-textarea--disabled":"hcr-textarea--disabled-1-6-0"}};

var Textarea = function Textarea(_ref) {
  var active = _ref.active,
      disabled = _ref.disabled,
      extraClasses = _ref.extraClasses,
      id = _ref.id,
      label = _ref.label,
      requiredText = _ref.requiredText,
      valid = _ref.valid,
      rest = _objectWithoutProperties(_ref, ["active", "disabled", "extraClasses", "id", "label", "requiredText", "valid"]);

  // defines base block class name for the input
  var bem = Bem('textarea', styles); // stores BEM class names, defines BEM modifiers

  var textareaClassNames = bem.b({
    valid: valid === true,
    error: valid === false,
    disabled: disabled,
    active: active,
    'no-label': !label
  });
  return React.createElement("div", {
    className: textareaClassNames
  }, label && React.createElement(Label, {
    extraClasses: bem.e('label'),
    labelFor: id,
    requiredText: requiredText,
    text: label
  }), React.createElement("textarea", _extends({
    id: id,
    disabled: disabled,
    required: requiredText ? true : _objectSpread2({}, rest).required,
    className: bem.e('field', false, extraClasses)
  }, rest)));
};
Textarea.displayName = 'Textarea';
Textarea.defaultProps = {
  active: null,
  disabled: false,
  extraClasses: '',
  requiredText: null,
  valid: null
};
Textarea.propTypes = {
  /** Triggers element visual active state */
  active: PropTypes.bool,

  /** Disables the textarea if set */
  disabled: PropTypes.bool,

  /** Custom classes name for element */
  extraClasses: PropTypes.string,

  /** Defines element ID that is used to bind it to the label */
  id: PropTypes.string.isRequired,

  /** Provides label text, required if aria-label is not passed */

  /* eslint-disable react/require-default-props */
  // we want label to be always checked against
  label: function label(_ref2) {
    var ariaLabel = _ref2['aria-label'],
        _label = _ref2.label;
    // if no aria label nor label throw validation error
    return !ariaLabel && !_label ? new Error('Either label or aria label are required') : null;
  },

  /** Marks the element as required, adds specified text to label text,
   * e.g. Phone (Mandatory) for required="(Mandatory)" */
  requiredText: PropTypes.string,

  /** Triggers visual validation output */
  valid: PropTypes.bool
};
var textarea = Injecss(Textarea, styles);

export { textarea as Textarea };
